'use client';

import { type ReactNode } from 'react';

import Image from 'next/image';
import Link from 'next/link';

import { Column, Grid } from '@carbon/react';

import { openCookiesPreferences } from '@/components/CookiePreferencesPlaceholder';
import ibmQuantumLogo from '@/images/IBM-Quantum-logo.png';
import { getLinkProps, getUrlType } from '@/utils/links';
import styles from './app-footer.module.scss';

export function AppFooter() {
  return (
    <footer className={styles['app-footer']} data-autotrackregion-namespace="Footer">
      <div className={styles['app-footer__main']}>
        <Grid>
          <Column sm={4} md={8}>
            <Link href="/" data-autotrack-cta="IBM Quantum Logo">
              <Image
                src={ibmQuantumLogo}
                alt="IBM Quantum logo"
                width={146}
                height={27}
                className={styles['app-footer__logo']}
              />
            </Link>
          </Column>
        </Grid>
        <Grid>
          <Column sm={4}>
            <div className={styles['app-footer__section-group']}>
              <p className={styles['app-footer__section-header']}>Featured</p>
              <FooterAction label="Hardware" url="/hardware" />
              <FooterAction label="IBM Quantum Safe" url="/quantum-safe" />
              <FooterAction label="IBM Quantum Network" url="/ibm-quantum-network" />
              <FooterAction label="Research" url="/research" />
            </div>
          </Column>
          <Column sm={4}>
            <div className={styles['app-footer__section-group']}>
              <p className={styles['app-footer__section-header']}>Get access</p>
              <FooterAction label="Product" url="/products" />
              <FooterAction label="IBM Quantum Platform" url="https://quantum.cloud.ibm.com/" />
            </div>
          </Column>
          <Column sm={4}>
            <div className={styles['app-footer__section-group']}>
              <p className={styles['app-footer__section-header']}>Get started</p>
              <FooterAction label="Qiskit" url="/qiskit" />
              <FooterAction label="Documentation" url="https://quantum.cloud.ibm.com/docs/" />
              <FooterAction label="Learning" url="https://quantum.cloud.ibm.com/learning" />
            </div>
          </Column>
          <Column sm={4}>
            <div className={styles['app-footer__section-group']}>
              <p className={styles['app-footer__section-header']}>Stay connected</p>
              <FooterAction label="Blog" url="/blog" />
              <FooterAction label="LinkedIn" url="https://www.linkedin.com/showcase/ibm-quantum/" />
              <FooterAction label="YouTube" url="https://www.youtube.com/qiskit" />
              <FooterAction label="Community" url="/ibm-quantum-network" />
              <FooterAction
                label="Careers"
                url="https://www.ibm.com/careers/us-en/search/?search=Quantum"
              />
            </div>
          </Column>
        </Grid>
      </div>
      <hr className={styles['app-footer__separator']} />
      <Grid>
        <Column sm={4} md={8} lg={16}>
          <div className={styles['app-footer__secondary']}>
            <FooterAction label="Contact IBM" url="https://www.ibm.com/contact?lnk=flg-cont-usen" />
            <FooterAction label="Terms of use" url="https://quantum.ibm.com/terms" />
            <FooterAction label="Accessibility" url="https://www.ibm.com/able/?lnk=flg-acce-usen" />
            <FooterAction label="Privacy" url="https://quantum.ibm.com/terms/privacy" />
            <FooterAction label="Cookie preferences" onClick={openCookiesPreferences} />
          </div>
        </Column>
      </Grid>
    </footer>
  );
}

type Action =
  | {
      url: string;
      onClick?: () => void;
    }
  | {
      url?: never;
      onClick?: () => void;
    };

export type FooterActionProps = {
  label: string;
  target?: string;
} & Action;

export function FooterAction(props: FooterActionProps): ReactNode {
  const { url, label, target: customTarget, ...rest } = props;

  if (url) {
    const { target: linkTarget, ...linkProps } = getLinkProps(url);
    const target = customTarget ?? linkTarget;
    const urlType = getUrlType(url);
    const isExternalUrl = urlType === 'corporate-external' || urlType === 'external';

    const LinkElement = isExternalUrl ? 'a' : Link;

    return (
      <LinkElement
        href={url}
        className={styles['app-footer__link']}
        target={target}
        {...linkProps}
        {...rest}
      >
        {label}
      </LinkElement>
    );
  }

  return (
    <button key={label} className={styles['app-footer__link']} {...rest}>
      {label}
    </button>
  );
}
